/* ============================================================
   LAYAM — storytelling motion layer (dependency-free)
   Scroll-reveal + stagger + parallax + scroll scrubbing +
   magnetic buttons + spotlight + counters + reading progress.
   Gated by the .la-motion class (set in <head> only when motion
   is allowed / not reduced-motion).

   Perf contract for anything added here:
   - never block scrolling (no non-passive wheel/touch handlers)
   - never interleave layout reads with style writes in a frame
   - animate transform/opacity only; no per-frame width/top/left
   ============================================================ */
(function () {
  'use strict';

  var docEl = document.documentElement;
  if (!docEl.classList.contains('la-motion')) return;

  // We're here, so the reveal logic below will run — stand down the inline
  // blank-page failsafe armed in <head>.
  if (window.__laRevealFailsafe) {
    clearTimeout(window.__laRevealFailsafe);
    window.__laRevealFailsafe = null;
  }

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE = window.matchMedia('(pointer: fine)').matches && !window.matchMedia('(hover: none)').matches;

  var slice = function (n) { return [].slice.call(n); };

  /* ------------------------------------------------------------
     1. ANCHOR JUMPS — native smooth scroll
     Previously this file ran a momentum scroll that hijacked the
     wheel: a non-passive preventDefault on every tick (which blocks
     the compositor) plus a getComputedStyle walk up the DOM per
     event to detect scrollable ancestors. That combination is what
     made scrolling feel heavy and lag behind the input. Native
     scrolling is faster, matches OS scroll physics, and keeps
     trackpad/touch momentum intact.
  ------------------------------------------------------------ */
  var HEADER_OFFSET = 78;

  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var href = a.getAttribute('href');
    if (!href || href === '#' || href.length < 2) return;
    var el = document.getElementById(href.slice(1));
    if (!el) return;
    e.preventDefault();
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET,
      behavior: REDUCE ? 'auto' : 'smooth'
    });
  });

  /* ------------------------------------------------------------
     2. SCROLL REVEAL + STAGGER
  ------------------------------------------------------------ */
  var revealEls = slice(document.querySelectorAll('[data-la-reveal]'));

  slice(document.querySelectorAll('[data-la-stagger]')).forEach(function (group) {
    slice(group.querySelectorAll('[data-la-reveal]')).forEach(function (k, i) {
      k.style.setProperty('--la-order', i);
    });
  });

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('la-in'); io.unobserve(en.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    revealEls.forEach(function (el) { io.observe(el); });
  } else {
    revealEls.forEach(function (el) { el.classList.add('la-in'); });
  }
  // Failsafe: never leave content hidden
  window.addEventListener('load', function () {
    setTimeout(function () { revealEls.forEach(function (el) { el.classList.add('la-in'); }); }, 2600);
  });

  /* ------------------------------------------------------------
     3. ANIMATED COUNTERS  [data-la-count="1200"] [data-la-suffix="+"]
  ------------------------------------------------------------ */
  slice(document.querySelectorAll('[data-la-count]')).forEach(function (el) {
    var end = parseFloat(el.getAttribute('data-la-count')) || 0;
    var suffix = el.getAttribute('data-la-suffix') || '';
    var prefix = el.getAttribute('data-la-prefix') || '';
    var dur = 1600, played = false;
    function run() {
      if (played) return; played = true;
      if (REDUCE) { el.textContent = prefix + end + suffix; return; }
      var t0 = null;
      function step(ts) {
        if (!t0) t0 = ts;
        var p = Math.min((ts - t0) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = prefix + Math.round(end * eased).toLocaleString() + suffix;
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }
    if ('IntersectionObserver' in window) {
      var co = new IntersectionObserver(function (ents) {
        ents.forEach(function (en) { if (en.isIntersecting) { run(); co.unobserve(en.target); } });
      }, { threshold: 0.6 });
      co.observe(el);
    } else { run(); }
  });

  /* ------------------------------------------------------------
     4. MAGNETIC BUTTONS + 5. SPOTLIGHT  (fine-pointer only)
     pointermove can fire faster than the display refreshes, so both
     effects cache their rect on enter and coalesce writes into one
     rAF per frame rather than measuring and restyling per event.
  ------------------------------------------------------------ */
  if (FINE && !REDUCE) {
    var pending = [];        // elements with a queued style write
    var pendingRaf = 0;

    function flush() {
      pendingRaf = 0;
      for (var i = 0; i < pending.length; i++) pending[i]._laWrite();
      pending.length = 0;
    }
    function queue(el) {
      if (el._laQueued) return;
      el._laQueued = true;
      pending.push(el);
      if (!pendingRaf) pendingRaf = requestAnimationFrame(flush);
    }

    slice(document.querySelectorAll('[data-la-magnetic], .layam-hero__ctas .button, .button--primary'))
      .forEach(function (el) {
        var strength = parseFloat(el.getAttribute('data-la-magnetic')) || 0.4;
        var rect = null, mx = 0, my = 0;

        el._laWrite = function () {
          el._laQueued = false;
          el.style.transform = 'translate(' + (mx * strength).toFixed(1) + 'px,' + (my * strength).toFixed(1) + 'px)';
        };
        el.addEventListener('pointerenter', function () {
          rect = el.getBoundingClientRect();
          el.style.willChange = 'transform';
        });
        el.addEventListener('pointermove', function (e) {
          if (!rect) rect = el.getBoundingClientRect();
          mx = e.clientX - (rect.left + rect.width / 2);
          my = e.clientY - (rect.top + rect.height / 2);
          queue(el);
        });
        el.addEventListener('pointerleave', function () {
          rect = null;
          el._laQueued = false;
          el.style.transform = '';
          el.style.willChange = '';   // don't hold a compositor layer for every button
        });
      });

    slice(document.querySelectorAll('[data-la-spotlight]')).forEach(function (el) {
      var rect = null, px = 0, py = 0;
      el._laWrite = function () {
        el._laQueued = false;
        el.style.setProperty('--mx', px.toFixed(1) + '%');
        el.style.setProperty('--my', py.toFixed(1) + '%');
      };
      el.addEventListener('pointerenter', function () { rect = el.getBoundingClientRect(); });
      el.addEventListener('pointermove', function (e) {
        if (!rect) rect = el.getBoundingClientRect();
        px = (e.clientX - rect.left) / rect.width * 100;
        py = (e.clientY - rect.top) / rect.height * 100;
        queue(el);
      });
      el.addEventListener('pointerleave', function () { rect = null; el._laQueued = false; });
    });
  }

  /* ------------------------------------------------------------
     6. PARALLAX + SCRUB + READING PROGRESS  (single rAF loop)
     [data-la-parallax="0.15"]  translate on scroll
     [data-la-scrub]            sets --la-progress 0→1 across viewport

     Every layout read in the frame happens before any style write,
     so the browser lays out once instead of once per element. Items
     outside the viewport are skipped, and the page height is cached
     rather than read (docEl.scrollHeight forces layout) each frame.
  ------------------------------------------------------------ */
  var parallax = slice(document.querySelectorAll('[data-la-parallax]')).map(function (el) {
    return { el: el, speed: parseFloat(el.getAttribute('data-la-parallax')) || 0.15, on: true, v: 0 };
  });
  var scrubs = slice(document.querySelectorAll('[data-la-scrub]')).map(function (el) {
    return { el: el, on: true, v: 0 };
  });
  var tracked = parallax.concat(scrubs);

  // Only do per-frame work for elements currently on screen.
  if ('IntersectionObserver' in window && tracked.length) {
    var vis = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        for (var i = 0; i < tracked.length; i++) {
          if (tracked[i].el === en.target) { tracked[i].on = en.isIntersecting; break; }
        }
      });
    }, { rootMargin: '15% 0px' });
    tracked.forEach(function (t) { vis.observe(t.el); });
  }

  var bar = null;
  if (!REDUCE) {
    bar = document.createElement('div');
    bar.className = 'la-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
  }

  var maxScroll = 0;
  function measurePage() { maxScroll = docEl.scrollHeight - window.innerHeight; }
  measurePage();

  var ticking = false;
  function frame() {
    ticking = false;
    var vh = window.innerHeight;
    var i, t, r;

    // ---- read phase: no style writes in this block ----
    if (!REDUCE) {
      for (i = 0; i < parallax.length; i++) {
        t = parallax[i];
        if (!t.on) continue;
        r = t.el.getBoundingClientRect();
        t.v = ((r.top + r.height / 2) - vh / 2) * -t.speed * 1.32;
      }
      for (i = 0; i < scrubs.length; i++) {
        t = scrubs[i];
        if (!t.on) continue;
        r = t.el.getBoundingClientRect();
        t.v = Math.max(0, Math.min(1, 1 - (r.top + r.height) / (vh + r.height)));
      }
    }
    var progress = maxScroll > 0 ? window.pageYOffset / maxScroll : 0;

    // ---- write phase ----
    if (!REDUCE) {
      for (i = 0; i < parallax.length; i++) {
        t = parallax[i];
        if (t.on) t.el.style.transform = 'translate3d(0,' + t.v.toFixed(1) + 'px,0)';
      }
      for (i = 0; i < scrubs.length; i++) {
        t = scrubs[i];
        if (t.on) t.el.style.setProperty('--la-progress', t.v.toFixed(4));
      }
    }
    // scaleX stays on the compositor; animating `width` relayouts every frame.
    if (bar) bar.style.transform = 'scaleX(' + progress.toFixed(4) + ')';
  }

  function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () { measurePage(); onScroll(); }, { passive: true });

  // Lazy images and web fonts change page height after load; re-measure then.
  window.addEventListener('load', function () { measurePage(); onScroll(); });
  if ('ResizeObserver' in window) {
    new ResizeObserver(measurePage).observe(document.body);
  }

  frame();
})();
